import {useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import * as turf from '@turf/turf'
import {useTranslation} from 'react-i18next'

import {KTSVG} from '../../../../_metronic/helpers'
import {openAlert} from '../../../../_metronic/elements/NotificationAlert'
import {useDrawV2} from '../Map/Draw/DrawV2Context'
import {DrawTools} from '../Map/Draw/DrawState'
import {mapState} from '../Map/MapState'
import {useMapLayersV2} from '../Map/MapLayersV2Context'
import DrawControl from '../Map/Draw/DrawControl'
import {formatHectares} from '../../../shared/numberFormat'
import './CreateFlow.style.scss'

const getDraftGeometry = (polygon) => {
  return polygon?.geometry ? polygon.geometry : polygon
}

const getDraftArea = (polygon) => {
  const geometry = getDraftGeometry(polygon)

  if (!geometry) {
    return 0
  }

  return turf.area(geometry) / 10000
}

const buildFieldEnvelopeGeometry = (polygons = []) => {
  if (polygons.length < 1) {
    return null
  }

  const features = polygons.reduce((result, polygon) => {
    const geometry = getDraftGeometry(polygon)

    if (!geometry) {
      return result
    }

    result.push({
      type: 'Feature',
      properties: {},
      geometry,
    })

    return result
  }, [])

  if (features.length < 1) {
    return null
  }

  let joinedPolygon = features[0]

  for (let i = 1; i < features.length; i += 1) {
    try {
      joinedPolygon = turf.union(joinedPolygon, features[i])
    } catch (error) {
      joinedPolygon = null
      break
    }
  }

  if (joinedPolygon?.geometry?.type === 'Polygon') {
    return joinedPolygon.geometry
  }

  const featureCollection = turf.featureCollection(features)
  const hull = turf.convex(featureCollection)

  if (hull?.geometry?.type === 'Polygon') {
    return hull.geometry
  }

  return turf.bboxPolygon(turf.bbox(featureCollection)).geometry
}

export const buildFieldGeometryFromDrafts = (polygons) => {
  return buildFieldEnvelopeGeometry(Object.values(polygons || {}))
}

const buildPendingLands = ({draftIds, polygonNames, polygonSize, polygons, t}) => {
  const pendingLands = []

  draftIds.forEach((id) => {
    const draftName = polygonNames[id]

    if (draftName == null || draftName.trim().length < 1) {
      openAlert('danger', t('lands:draw.errors.title'), t('lands:create.form.errors.name_required'))
      throw Error('Missing name')
    }

    if (draftName.length > 64) {
      openAlert('danger', t('lands:draw.errors.title'), t('lands:create.form.errors.name_too_long'))
      throw Error('Name too long')
    }

    const size = polygonSize[id]
    if (size < 5) {
      openAlert('danger', t('lands:draw.errors.title'), t('lands:create.form.errors.min_ha'))
      throw Error('Min ha error')
    }

    const polygon = polygons[id]
    const geometry = getDraftGeometry(polygon)
    pendingLands.push({
      name: draftName.trim(),
      polygon: geometry,
    })
  })

  if (pendingLands.length < 1) {
    openAlert('danger', t('lands:draw.errors.title'), t('lands:create.form.errors.one_required'))
    throw Error('One land required')
  }

  return pendingLands
}

const DraftCard = ({id, index, name, size, polygon, onNameChange, onRemove, onFocus}) => {
  const {t} = useTranslation()
  const displayIndex = index + 1

  return (
    <div className='gis-create-flow-draft' key={id}>
      <button
        type='button'
        className='btn btn-color-muted btn-active-color-primary btn-sm field-view-card__focus gis-create-flow-draft__icon-button gis-create-flow-draft__focus-button'
        onClick={() => onFocus(polygon)}
        title={t('lands:create.buttons.focus')}
        aria-label={t('lands:create.buttons.focus')}
      >
        <KTSVG
          path='/media/icons/duotune/maps/map007.svg'
          className='svg-icon svg-icon-1 field-view-card__focus-icon'
        />
      </button>

      <div className='gis-create-flow-draft__field'>
        <p className='gis-create-flow-draft__label'>
          {t('lands:create.draft.name', {index: displayIndex})}
          <span>{formatHectares(size)}</span>
        </p>
        <input
          required
          type='text'
          className='form-control gis-create-flow-draft__input'
          aria-label={t('lands:create.draft.name_aria', {index: displayIndex})}
          onFocus={() => onFocus(polygon)}
          onChange={(event) => onNameChange(id, event.target.value)}
          value={name}
        />
      </div>

      <button
        type='button'
        className='btn btn-light-danger gis-create-flow-draft__icon-button'
        onClick={() => onRemove(id, true)}
        title={t('lands:create.buttons.remove')}
        aria-label={t('lands:create.buttons.remove')}
      >
        <KTSVG path='/media/icons/duotune/art/art004.svg' className='svg-icon svg-icon-2' />
      </button>

      {size < 5 && (
        <div className='gis-create-flow-hint gis-create-flow-hint--warning'>
          <p className='gis-create-flow-hint__copy'>{t('lands:create.form.errors.min_ha')}</p>
        </div>
      )}
    </div>
  )
}

const MapDrawingGuide = ({visible, mode, pointCount}) => {
  const {t} = useTranslation()
  const message = useMemo(() => {
    if (mode === DrawTools.CIRCLE) {
      return t('lands:draw.guide_circle')
    }

    if (pointCount < 1) {
      return t('lands:draw.guide_first')
    }

    if (pointCount < 3) {
      return t('lands:draw.guide_continue')
    }

    return t('lands:draw.guide_finish')
  }, [mode, pointCount, t])

  if (!visible || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div className='gis-map-guidance'>
      <div className='gis-map-guidance__card'>{message}</div>
    </div>,
    document.body
  )
}

export const LandDraftBuilder = ({
  title,
  description,
  startIndex = 0,
  submitLabel,
  cancelLabel,
  loading = false,
  onCancel,
  onSubmit,
}) => {
  const {t} = useTranslation()
  const [polygons, setPolygons] = useState({})
  const [polygonNames, setPolygonNames] = useState({})
  const [polygonSize, setPolygonSize] = useState({})
  const [drawingPointCount, setDrawingPointCount] = useState(0)
  const counter = useRef(startIndex)
  const draw = useDrawV2()
  const {startDrawing, endDrawing, deleteLayer, currentMode} = draw
  const mapLayers = useMapLayersV2()
  const resolvedCancelLabel = cancelLabel || t('common:actions.cancel')
  const landToolLabels = useMemo(
    () => ({
      [DrawTools.POLYGON]: t('lands:draw.polygon'),
      [DrawTools.CIRCLE]: t('lands:draw.circle'),
      [DrawTools.UPLOAD_FILE]: t('lands:draw.upload'),
    }),
    [t]
  )

  const draftIds = useMemo(() => Object.keys(polygonNames), [polygonNames])
  const hasDrafts = draftIds.length > 0
  const totalDrawnHa = useMemo(() => {
    return draftIds.reduce((total, id) => total + (polygonSize[id] || 0), 0)
  }, [draftIds, polygonSize])

  const deletePolygon = (id, shouldDeleteLayer = false) => {
    setPolygons((prev) => {
      const next = {...prev}
      delete next[id]
      return next
    })
    setPolygonNames((prev) => {
      const next = {...prev}
      delete next[id]
      return next
    })
    setPolygonSize((prev) => {
      const next = {...prev}
      delete next[id]
      return next
    })

    if (shouldDeleteLayer) {
      deleteLayer(id)
    }
  }

  const fitPolygon = (polygon) => {
    const geometry = getDraftGeometry(polygon)

    if (!geometry) {
      return
    }

    mapLayers.fitToPolygon(geometry)
  }

  const updateName = (id, nextName) => {
    setPolygonNames((prev) => ({...prev, [id]: nextName}))
  }

  useEffect(() => {
    counter.current = startIndex

    startDrawing({
      tools: [DrawTools.POLYGON, DrawTools.CIRCLE, DrawTools.UPLOAD_FILE],
      keepModeAfterCreate: true,
      onCreate: (event) => {
        const nextDrafts = Array.isArray(event.features) ? event.features : []
        setDrawingPointCount(0)

        if (nextDrafts.length < 1) {
          return
        }

        setPolygons((prev) => {
          const next = {...prev}
          nextDrafts.forEach((draft) => {
            next[draft.id] = draft
          })
          return next
        })
        setPolygonSize((prev) => {
          const next = {...prev}
          nextDrafts.forEach((draft) => {
            next[draft.id] = getDraftArea(draft)
          })
          return next
        })
        setPolygonNames((prev) => {
          const next = {...prev}
          nextDrafts.forEach((draft) => {
            next[draft.id] =
              draft.properties?.name ||
              t('lands:create.draft.name', {index: 1 + counter.current})
            counter.current += 1
          })
          return next
        })
      },
      onUpdate: (event) => {
        const nextDrafts = Array.isArray(event.features) ? event.features : []

        if (nextDrafts.length < 1) {
          return
        }

        setPolygons((prev) => {
          const next = {...prev}
          nextDrafts.forEach((draft) => {
            next[draft.id] = draft
          })
          return next
        })
        setPolygonSize((prev) => {
          const next = {...prev}
          nextDrafts.forEach((draft) => {
            next[draft.id] = getDraftArea(draft)
          })
          return next
        })
      },
      onDelete: (event) => {
        const deletedDrafts = Array.isArray(event.features) ? event.features : []
        setDrawingPointCount(0)

        if (deletedDrafts.length < 1) {
          return
        }

        setPolygons((prev) => {
          const next = {...prev}
          deletedDrafts.forEach((draft) => {
            delete next[draft.id]
          })
          return next
        })
        setPolygonNames((prev) => {
          const next = {...prev}
          deletedDrafts.forEach((draft) => {
            delete next[draft.id]
          })
          return next
        })
        setPolygonSize((prev) => {
          const next = {...prev}
          deletedDrafts.forEach((draft) => {
            delete next[draft.id]
          })
          return next
        })
      },
    })

    return () => {
      endDrawing()
    }
  }, [endDrawing, startDrawing, startIndex, t])

  useEffect(() => {
    setDrawingPointCount(0)

    if (currentMode !== DrawTools.POLYGON) {
      return
    }

    let disposed = false
    let timerId = null
    let container = null

    const onCanvasClick = () => {
      setDrawingPointCount((previousCount) => previousCount + 1)
    }

    const bindCanvasClicks = () => {
      if (disposed) {
        return
      }

      const nextContainer = mapState?.map?.getCanvasContainer?.()

      if (!(nextContainer instanceof HTMLElement)) {
        timerId = window.setTimeout(bindCanvasClicks, 120)
        return
      }

      container = nextContainer
      container.addEventListener('click', onCanvasClick)
    }

    bindCanvasClicks()

    return () => {
      disposed = true

      if (timerId != null) {
        window.clearTimeout(timerId)
      }

      if (container instanceof HTMLElement) {
        container.removeEventListener('click', onCanvasClick)
      }
    }
  }, [currentMode])

  const handleSubmit = async () => {
    let pendingLands = []
    let unionGeometry = null

    try {
      pendingLands = buildPendingLands({
        draftIds,
        polygonNames,
        polygonSize,
        polygons,
        t,
      })
      unionGeometry = buildFieldGeometryFromDrafts(polygons)
    } catch (error) {
      // Validation alerts are already surfaced inline; avoid bubbling noisy promise rejections.
      return
    }

    await onSubmit({
      pendingLands,
      unionGeometry,
    })
  }

  return (
    <>
      <MapDrawingGuide
        visible={currentMode === DrawTools.POLYGON || currentMode === DrawTools.CIRCLE}
        mode={currentMode}
        pointCount={drawingPointCount}
      />
      <div className='card shadow-sm field-view-container right-card-data gis-create-flow-card gis-create-flow-card--guided'>
        <div className='card-header'>
          <h4 className='gis-create-flow-title'>{title}</h4>
          {description ? <p className='gis-create-flow-copy'>{description}</p> : null}
        </div>

        <div className='card-body'>
          <div className='gis-create-flow-action-bar'>
            <DrawControl
              className='map-draw-control--wizard'
              showLabel={false}
              toolLabels={landToolLabels}
            />
          </div>

          {hasDrafts ? (
            <>
              <div className='gis-create-flow-review-bar'>
                <span>
                  {t(
                    draftIds.length === 1
                      ? 'lands:create.draft.count_one'
                      : 'lands:create.draft.count_other',
                    {count: draftIds.length}
                  )}
                </span>
                <strong>{formatHectares(totalDrawnHa)}</strong>
              </div>

              <div className='gis-create-flow-drafts'>
                {draftIds.map((id, index) => (
                  <DraftCard
                    key={id}
                    id={id}
                    index={index}
                    name={polygonNames[id]}
                    size={polygonSize[id] || 0}
                    polygon={polygons[id]}
                    onNameChange={updateName}
                    onRemove={deletePolygon}
                    onFocus={fitPolygon}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className='card-footer'>
          <button
            type='button'
            className='btn btn-light gis-create-flow-footer-button'
            onClick={onCancel}
            disabled={loading}
          >
            {resolvedCancelLabel}
          </button>
          <button
            type='button'
            className='btn btn-primary gis-create-flow-footer-button'
            onClick={handleSubmit}
            disabled={!hasDrafts || loading}
          >
            {!loading && submitLabel}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                {t('lands:create.buttons.saving')}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  )
}
